import React from 'react';

import {
    getStoredSession,
    setStoredSession,
    signOut
} from '../../lib/supabase-auth.js';

import {cloudflareConfig} from '../../lib/cloudflare-config.js';
import styles from './home-page.css';

const navigate = path => {
    window.location.assign(path);
};

const HomePage = () => {
    const [session, setSession] = React.useState(() => getStoredSession());
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [projects, setProjects] = React.useState([]);
    const [loadingProjects, setLoadingProjects] = React.useState(true);

    React.useEffect(() => {
        const syncSession = () => setSession(getStoredSession());
        window.addEventListener('storage', syncSession);
        return () => window.removeEventListener('storage', syncSession);
    }, []);

    React.useEffect(() => {
        const handleClickAway = event => {
            if (!(event.target instanceof Element) || !event.target.closest('[data-bitbyte-home-menu]')) {
                setMenuOpen(false);
            }
        };
        window.addEventListener('click', handleClickAway);
        return () => window.removeEventListener('click', handleClickAway);
    }, []);

    React.useEffect(() => {
        fetch(cloudflareConfig.projectHost)
            .then(res => res.json())
            .then(data => {
                setProjects(data.projects || []);
                setLoadingProjects(false);
            })
            .catch(err => {
                console.error('Failed to fetch community projects:', err);
                setLoadingProjects(false);
            });
    }, []);

    const userName = session?.user?.user_metadata?.name || session?.user?.user_metadata?.username || 'Creator';

    const handleLogout = async () => {
        if (session?.access_token) {
            await signOut(session.access_token);
        } else {
            setStoredSession(null);
        }
        setSession(null);
        setMenuOpen(false);
        navigate('/');
    };

    return (
        <div className={styles.page}>
            <header className={styles.topBar}>
                <button
                    className={styles.brand}
                    type="button"
                    onClick={() => navigate('/')}
                >
                    <img className={styles.brandMark} src="/static/favicon.svg" alt="" aria-hidden="true" />
                    BitByte
                </button>

                <div className={styles.topActions}>
                    {session ? (
                        <div className={styles.userArea} data-bitbyte-home-menu>
                            <button
                                className={styles.userButton}
                                type="button"
                                onClick={() => setMenuOpen(prev => !prev)}
                            >
                                <span className={styles.userName}>{userName}</span>
                                <span className={styles.caret}>▾</span>
                            </button>
                            {menuOpen ? (
                                <div className={styles.dropdown}>
                                    <button className={styles.dropdownItem} type="button" onClick={() => navigate('/settings')}>Settings</button>
                                    <button className={styles.dropdownItem} type="button" onClick={() => navigate('/editor')}>Editor</button>
                                    <button className={styles.dropdownItem} type="button" onClick={handleLogout}>Log out</button>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <>
                            <button className={styles.secondaryButton} type="button" onClick={() => navigate('/login')}>Log in</button>
                            <button className={styles.primaryButton} type="button" onClick={() => navigate('/signup')}>Sign up</button>
                        </>
                    )}
                </div>
            </header>

            <main className={styles.hero}>
                <div className={styles.heroCopy}>
                    <p className={styles.kicker}>BitByte</p>
                    <h1 className={styles.title}>A playful place to build, learn, and ship ideas.</h1>
                    <p className={styles.subtitle}>
                        Open the editor to create projects, or sign in to keep your work and AI history in sync.
                    </p>
                    <div className={styles.buttonRow}>
                        <button className={styles.primaryButtonLarge} type="button" onClick={() => navigate('/editor')}>
                            Open editor
                        </button>
                        {!session ? (
                            <button className={styles.secondaryButtonLarge} type="button" onClick={() => navigate('/login')}>
                                Log in
                            </button>
                        ) : (
                            <button className={styles.secondaryButtonLarge} type="button" onClick={() => navigate('/settings')}>
                                Settings
                            </button>
                        )}
                    </div>
                </div>

                <section className={styles.featureCard}>
                    <div className={styles.featureLabel}>What is BitByte?</div>
                    <div className={styles.featureText}>
                        A polished, kid-friendly creative studio with blocks, AI help, and a cleaner workspace.
                    </div>
                </section>

                <section className={styles.communitySection}>
                    <h2 className={styles.sectionTitle}>Community Projects</h2>
                    {loadingProjects ? (
                        <p>Loading projects...</p>
                    ) : (
                        <div className={styles.projectGrid}>
                            {projects.length > 0 ? (
                                projects.map(project => (
                                    <button
                                        key={project.id}
                                        className={styles.projectCard}
                                        type="button"
                                        onClick={() => navigate(`/editor#${project.id}`)}
                                    >
                                        <div className={styles.projectTitle}>{project.title}</div>
                                        <div className={styles.projectMeta}>
                                            {new Date(project.updated_at).toLocaleDateString()}
                                        </div>
                                    </button>
                                ))
                            ) : (
                                <p>No projects shared yet. Be the first!</p>
                            )}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default HomePage;
